import { apiErrorMessage } from '@agos/api-client'
import {
  getGetSuperAdminAdminsQueryKey,
  useDeleteSuperAdminFranchisesFranchiseIdAdminsAdminId,
  useGetSuperAdminAdmins,
  useGetSuperAdminFranchises,
  usePatchSuperAdminFranchisesFranchiseIdAdminsAdminId,
} from '@agos/api-client/platform'
import { T, tt } from '@agos/shared'
import { PageHeader, Spinner, StatusBadge, toast } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { KeyRound, UserCog, UserX } from 'lucide-react'
import { useMemo, useState } from 'react'

import { franchisesOf } from '../franchises/franchises'
import {
  adminsOf,
  deactivateDescription,
  deactivateTitle,
  groupedByFranchise,
  loadNote,
  noAdminsNote,
  passwordConfirmation,
  passwordPayload,
  passwordProblems,
  reactivatePayload,
  type FranchiseAdmin,
} from './franchise-admins'

/**
 * `/franchise-admins` — every administrator across every franchise.
 *
 * **This page does not poll**, and that is deliberate: `GET /super-admin/admins` gathers its answer
 * *"one tenant at a time"* — a query per active franchise, per request. It refetches after a write and
 * at no other time. FB-P02.
 */
export function FranchiseAdminsPage() {
  const queryClient = useQueryClient()
  const { data, isLoading } = useGetSuperAdminAdmins()
  const { data: franchiseData } = useGetSuperAdminFranchises()

  const admins = useMemo(() => adminsOf(data), [data])
  const franchises = useMemo(() => franchisesOf(franchiseData), [franchiseData])
  const groups = useMemo(() => groupedByFranchise(admins, franchises), [admins, franchises])

  const [deactivating, setDeactivating] = useState<FranchiseAdmin | null>(null)
  const [changing, setChanging] = useState<FranchiseAdmin | null>(null)
  const [password, setPassword] = useState('')
  const [repeated, setRepeated] = useState('')

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: getGetSuperAdminAdminsQueryKey() })
  }

  const complain = (caught: unknown) =>
    toast({ variant: 'destructive', title: tt('Nepodarilo sa'), description: apiErrorMessage(caught, '') })

  const deactivate = useDeleteSuperAdminFranchisesFranchiseIdAdminsAdminId({
    mutation: {
      onSuccess: () => {
        refresh()
        setDeactivating(null)
        toast({ title: tt('Administrátor deaktivovaný') })
      },
      onError: complain,
    },
  })

  const patch = usePatchSuperAdminFranchisesFranchiseIdAdminsAdminId({
    mutation: {
      onSuccess: () => {
        refresh()
        setChanging(null)
        // Never kept, never echoed back into the field.
        setPassword('')
        setRepeated('')
        toast({ title: tt('Hotovo') })
      },
      onError: complain,
    },
  })

  const problems = changing === null ? [] : passwordProblems(password, repeated)

  return (
    <div className="space-y-5" data-franchise-admins>
      <PageHeader
        title={tt('Administrátori pobočiek')}
        description={loadNote(groups)}
        icon={<UserCog className="w-5 h-5" />}
      />

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : (
        groups.map((group) => (
          <section key={group.franchiseId} className="space-y-2" data-franchise-group={group.franchiseId}>
            <h3 className="text-sm font-bold">{group.franchiseName}</h3>
            {group.admins.length === 0 ? (
              <p className="text-[11px] text-amber-500" data-no-admins={group.franchiseId}>
                {noAdminsNote()}
              </p>
            ) : (
              <ul className="space-y-2">
                {group.admins.map((admin) => (
                  <li
                    key={admin.id}
                    data-admin={admin.id}
                    className="rounded-2xl border border-border bg-card p-3 flex flex-wrap items-center gap-3"
                  >
                    <div className="flex-1 min-w-[12rem]">
                      <p className="text-sm font-semibold">
                        {admin.name}
                        {!admin.active && (
                          <span className="ml-2">
                            <StatusBadge color="zinc" label={tt('deaktivovaný')} />
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {admin.email} · {admin.systemRole}
                      </p>
                    </div>
                    <button
                      type="button"
                      data-change-password={admin.id}
                      onClick={() => {
                        setPassword('')
                        setRepeated('')
                        setChanging(admin)
                      }}
                      className="min-h-[40px] px-3 rounded-xl border border-border text-sm font-semibold flex items-center gap-1.5"
                    >
                      <KeyRound className="w-3.5 h-3.5" /> <T>Heslo</T>
                    </button>
                    {admin.active ? (
                      <button
                        type="button"
                        data-deactivate-admin={admin.id}
                        onClick={() => setDeactivating(admin)}
                        className="min-h-[40px] px-3 rounded-xl border border-border text-sm font-semibold flex items-center gap-1.5 text-destructive"
                      >
                        <UserX className="w-3.5 h-3.5" /> <T>Deaktivovať</T>
                      </button>
                    ) : (
                      <button
                        type="button"
                        data-reactivate-admin={admin.id}
                        onClick={() =>
                          patch.mutate({
                            franchiseId: admin.franchiseId,
                            adminId: admin.id,
                            data: reactivatePayload(),
                          })
                        }
                        className="min-h-[40px] px-3 rounded-xl border border-border text-sm font-semibold"
                      >
                        <T>Znova zapnúť</T>
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))
      )}

      {deactivating !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-deactivate-dialog>
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            aria-label={tt('Zavrieť')}
            onClick={() => setDeactivating(null)}
          />
          <section className="relative w-full max-w-md rounded-2xl bg-background border border-border p-5 space-y-3">
            <h2 className="text-base font-bold">{deactivateTitle()}</h2>
            <p className="text-xs text-muted-foreground" data-deactivate-description>
              {deactivateDescription(deactivating)}
            </p>
            <button
              type="button"
              data-deactivate-confirm
              disabled={deactivate.isPending}
              onClick={() =>
                deactivate.mutate({
                  franchiseId: deactivating.franchiseId,
                  adminId: deactivating.id,
                })
              }
              className="w-full min-h-[48px] rounded-xl bg-primary text-primary-foreground font-bold disabled:opacity-50"
            >
              {deactivate.isPending ? <Spinner /> : deactivateTitle()}
            </button>
          </section>
        </div>
      )}

      {changing !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-password-dialog>
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            aria-label={tt('Zavrieť')}
            onClick={() => setChanging(null)}
          />
          <section className="relative w-full max-w-md rounded-2xl bg-background border border-border p-5 space-y-3">
            <h2 className="text-base font-bold">
              <T>Nové heslo</T>
            </h2>
            {/* Named by address: eight administrators of one franchise can be two characters apart. */}
            <p className="text-xs text-muted-foreground" data-password-for>
              {passwordConfirmation(changing)}
            </p>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              aria-label={tt('Nové heslo')}
              autoComplete="new-password"
              data-new-password
              className="w-full min-h-[44px] rounded-xl bg-card border border-border px-3 text-sm"
            />
            <input
              type="password"
              value={repeated}
              onChange={(event) => setRepeated(event.target.value)}
              aria-label={tt('Zopakujte heslo')}
              autoComplete="new-password"
              data-repeat-password
              className="w-full min-h-[44px] rounded-xl bg-card border border-border px-3 text-sm"
            />
            {problems.map((problem) => (
              <p key={problem} className="text-[11px] text-destructive" data-password-problem>
                {problem}
              </p>
            ))}
            <button
              type="button"
              data-password-save
              disabled={problems.length > 0 || password === '' || patch.isPending}
              onClick={() =>
                patch.mutate({
                  franchiseId: changing.franchiseId,
                  adminId: changing.id,
                  data: passwordPayload(password),
                })
              }
              className="w-full min-h-[48px] rounded-xl bg-primary text-primary-foreground font-bold disabled:opacity-50"
            >
              {patch.isPending ? <Spinner /> : <T>Nastaviť heslo</T>}
            </button>
          </section>
        </div>
      )}
    </div>
  )
}
